import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { HookExecutionTester } from '@/components/test/HookExecutionTester';
import { usePerformanceMetrics } from '@/hooks/usePerformanceMetrics';

/**
 * Test page for validating React hooks execution order across different states
 * This page demonstrates our best practices for handling React hooks
 * to ensure consistent execution regardless of component state
 */
export default function TestHookExecution() {
  // Use our performance metrics hook to measure component performance
  const { measureRender, markInteractive } = usePerformanceMetrics('TestHookExecution');
  
  // Start measuring render time
  const endRenderMeasure = measureRender();
  
  // State for toggling multiple instances
  const [showMultipleTesters, setShowMultipleTesters] = useState(false);
  
  // Mark component as interactive after initial render
  markInteractive();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white py-12 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>React Hooks Execution Test | Meduler</title>
        <meta name="description" content="Testing proper hook execution order in React components" />
      </Helmet>
      
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            React Hooks Execution Test
          </h1>
          <p className="mt-3 text-xl text-gray-500">
            Validating consistent hook execution order across different view modes and states
          </p>
        </div>
        
        <div className="mb-8 p-4 border rounded-md bg-blue-50">
          <h2 className="text-xl font-bold text-blue-800 mb-2">How This Test Works</h2>
          <p className="mb-4">
            This test demonstrates our fix for the React hooks execution order issues. It verifies that:
          </p>
          <ul className="list-disc pl-6 space-y-1 text-blue-700">
            <li>All hooks execute in the same order on every render</li>
            <li>Conditional rendering does not affect hook execution order</li>
            <li>State changes maintain consistent execution patterns</li>
            <li>Performance measurement doesn't interfere with component behavior</li>
          </ul>
        </div>
        
        <div className="mb-8">
          <button
            onClick={() => setShowMultipleTesters(prev => !prev)}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50"
          >
            {showMultipleTesters ? 'Show Single Tester' : 'Show Multiple Testers'}
          </button>
        </div>
        
        {showMultipleTesters ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <HookExecutionTester />
            <HookExecutionTester />
          </div>
        ) : (
          <HookExecutionTester />
        )}
        
        <div className="mt-12 p-6 border rounded-lg shadow-md bg-white">
          <h2 className="text-2xl font-bold mb-4">Best Practices for React Hooks</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-2 text-red-600">❌ Incorrect Pattern</h3>
              <pre className="bg-gray-800 text-white p-4 rounded overflow-auto text-sm">
                {`function BadComponent() {
  const [isLoading, setIsLoading] = useState(true);
  
  // This early return creates inconsistent execution
  if (isLoading) {
    return <LoadingSpinner />;
  }
  
  // This hook won't run when loading is true!
  useEffect(() => {
    fetchData();
  }, []);
  
  return <MainContent />;
}`}
              </pre>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-2 text-green-600">✅ Correct Pattern</h3>
              <pre className="bg-gray-800 text-white p-4 rounded overflow-auto text-sm">
                {`function GoodComponent() {
  const [isLoading, setIsLoading] = useState(true);
  
  // All hooks run in consistent order on every render
  useEffect(() => {
    fetchData();
  }, []);
  
  // Use conditional rendering instead of early return
  return (
    <>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <MainContent />
      )}
    </>
  );
}`}
              </pre>
            </div>
          </div>
        </div>
      </div>
      
      {/* End render measurement at the end of the component */}
      {endRenderMeasure()}
    </div>
  );
}
